import React from "react";
import {
  AppBar, Toolbar, Box, Typography, Chip, Button,
  Avatar, Menu, MenuItem, Divider,
} from "@mui/material";
import { CreditCard, LogOut, History, Info, Mail, Zap, LayoutDashboard, Receipt } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout, selectUser } from "../../store/slices/authSlice";
import HyrvixLogo from "../HyrvixLogo";

export default function Navbar() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const isPaid = user?.plan_type === "paid";
  const credits = user?.credits;

  // Chip shown for all users — always display credit balance
  const creditsChip = () => {
    if (credits === -1) return { label: "Pro ✓", color: "#dcfce7", textColor: "#15803d" };
    if (credits > 5)  return { label: `⚡ ${credits} credits`, color: "#dcfce7", textColor: "#15803d" };
    if (credits > 0)  return { label: `⚡ ${credits} credit${credits === 1 ? "" : "s"}`, color: "#fef9c3", textColor: "#92400e" };
    return { label: "0 credits", color: "#fee2e2", textColor: "#b91c1c" };
  };

  const chip = creditsChip();

  const handleLogout = async () => {
    setAnchorEl(null);
    await dispatch(logout());
    navigate("/login");
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: { xs: "#ffffff", md: "rgba(255,255,255,0.85)" },
        backdropFilter: { xs: "none", md: "blur(12px)" },
        borderBottom: "1px solid #e2e8f0",
        color: "#0f172a",
      }}
    >
      <Toolbar sx={{ gap: 2 }}>
        <Box
          onClick={() => navigate("/")}
          sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1, cursor: "pointer" }}
        >
          <HyrvixLogo size={32} />
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.3px" }}>
            Hyrvix
          </Typography>
        </Box>

        {/* Desktop nav links */}
        <Box sx={{ display: { xs: "none", md: "flex" }, gap: 0.5 }}>
          {[
            { label: "Home", path: "/" },
            { label: "Optimizer", path: "/dashboard" },
            { label: "History", path: "/history" },
            { label: "Pricing", path: "/pricing" },
          ].map((item) => (
            <Button key={item.label} size="small"
              onClick={() => navigate(item.path)}
              sx={{ color: "#64748b", fontWeight: 600, fontSize: 13, borderRadius: "8px", px: 1.5,
                "&:hover": { bgcolor: "#f1f5f9", color: "#0f172a" } }}
            >
              {item.label}
            </Button>
          ))}
        </Box>

        <Chip
          label={chip.label}
          size="small"
          sx={{ bgcolor: chip.color, color: chip.textColor, fontWeight: 700, border: "none" }}
        />
        <Button
          size="small" variant="outlined"
          onClick={() => navigate("/pricing")}
          startIcon={<Zap size={13} />}
          sx={{ borderRadius: "8px", borderColor: "#e2e8f0", color: "#64748b", fontWeight: 600, fontSize: 12 }}
        >
          {credits > 0 || credits === -1 ? "Buy More" : "Get Credits"}
        </Button>

        <Avatar
          sx={{ cursor: "pointer", bgcolor: "#2563eb", width: 34, height: 34, fontSize: 14, fontWeight: 700 }}
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          {user?.email?.charAt(0).toUpperCase()}
        </Avatar>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: { xs: "0px", md: "12px" },
                boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                minWidth: 200,
                mt: 1,
              },
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>Signed in as</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }}>{user?.email}</Typography>
          </Box>
          <Divider />
          <MenuItem onClick={() => { setAnchorEl(null); navigate("/dashboard"); }} sx={{ gap: 1.5, py: 1.2 }}>
            <LayoutDashboard size={16} /><Typography variant="body2">Resume Optimizer</Typography>
          </MenuItem>
          <MenuItem onClick={() => { setAnchorEl(null); navigate("/history"); }} sx={{ gap: 1.5, py: 1.2 }}>
            <History size={16} /><Typography variant="body2">My Generations</Typography>
          </MenuItem>
          <MenuItem onClick={() => { setAnchorEl(null); navigate("/billing"); }} sx={{ gap: 1.5, py: 1.2 }}>
            <Receipt size={16} /><Typography variant="body2">Billing & Invoices</Typography>
          </MenuItem>
          <MenuItem onClick={() => { setAnchorEl(null); navigate("/pricing"); }} sx={{ gap: 1.5, py: 1.2 }}>
            <CreditCard size={16} /><Typography variant="body2">{isPaid ? "Buy More Credits" : "Pricing"}</Typography>
          </MenuItem>
          <MenuItem onClick={() => { setAnchorEl(null); navigate("/about"); }} sx={{ gap: 1.5, py: 1.2 }}>
            <Info size={16} /><Typography variant="body2">About</Typography>
          </MenuItem>
          <MenuItem onClick={() => { setAnchorEl(null); navigate("/contact"); }} sx={{ gap: 1.5, py: 1.2 }}>
            <Mail size={16} /><Typography variant="body2">Contact Us</Typography>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ gap: 1.5, py: 1.2, color: "#ef4444" }}>
            <LogOut size={16} /><Typography variant="body2">Sign out</Typography>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
